import { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InboxIcon from '@mui/icons-material/Inbox';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import ForestIcon from '@mui/icons-material/Forest';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { useAuthStore } from '@/store/auth.store';
import { authApi } from '@/api/auth.api';
import { useLanguage } from '@/i18n/LanguageContext';
import { LanguageSelector } from './LanguageSelector';
import { getInitials } from '@/utils/format';

const DRAWER_WIDTH = 240;

export function AppLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const { user, refreshToken, logout } = useAuthStore();
  const { ui } = useLanguage();
  const navigate = useNavigate();

  const restricted = user?.role === 'EVENT_REQUEST';
  const NAV: { to: string; label: string; icon: React.ReactNode; end?: boolean }[] = restricted
    ? [
        { to: '/admin/inicio', label: ui('home.staff.heading'), icon: <DashboardIcon /> },
        { to: '/admin/eventos/registrar', label: ui('home.staff.event'), icon: <EventNoteIcon /> },
        { to: '/admin/solicitudes/registrar', label: ui('admin.newInternalRequest'), icon: <PostAddIcon /> },
      ]
    : [
    { to: '/admin/dashboard', label: ui('admin.dashboard'), icon: <DashboardIcon /> },
    { to: '/admin/solicitudes', label: ui('admin.requests'), icon: <InboxIcon />, end: true },
    { to: '/admin/solicitudes/nueva', label: ui('admin.newInternalRequest'), icon: <PostAddIcon /> },
    { to: '/admin/eventos', label: ui('admin.events'), icon: <EventNoteIcon /> },
    ...(user?.role === 'ADMIN' ? [{ to: '/admin/usuarios', label: ui('admin.users'), icon: <ManageAccountsIcon /> }] : []),
  ];

  const handleLogout = async () => {
    try {
      if (refreshToken) await authApi.logout(refreshToken);
    } finally {
      logout();
      navigate('/login');
    }
  };

  const drawer = (
    <Box display="flex" flexDirection="column" height="100%">
      <Toolbar sx={{ gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <ForestIcon color="primary" />
        <Box lineHeight={1.1}>
          <Typography fontWeight={700} color="primary.main">{ui('app.title')}</Typography>
          <Typography variant="caption" color="text.secondary">{ui('admin.title')}</Typography>
        </Box>
      </Toolbar>
      <List sx={{ flex: 1, px: 1, py: 1.5 }}>
        {NAV.map(({ to, label, icon, end }) => (
          <ListItemButton
            key={to}
            component={NavLink}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              '&.active': { bgcolor: 'primary.main', color: 'primary.contrastText', '& .MuiListItemIcon-root': { color: 'inherit' } },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>{icon}</ListItemIcon>
            <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <Box p={2}>
        <LanguageSelector />
      </Box>
    </Box>
  );

  return (
    <Box display="flex" minHeight="100vh" bgcolor="background.default">
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { md: DRAWER_WIDTH }, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" onClick={() => setOpen(true)} sx={{ mr: 1 }} aria-label="menu">
              <MenuIcon />
            </IconButton>
          )}
          <Box flex={1} />
          {/* Visible sign-out (the avatar menu keeps it too). Icon only on phones. */}
          {isMobile ? (
            <IconButton onClick={handleLogout} aria-label={ui('admin.signOut')} sx={{ mr: 0.5 }}>
              <LogoutIcon />
            </IconButton>
          ) : (
            <Button onClick={handleLogout} variant="outlined" size="small" startIcon={<LogoutIcon />} sx={{ mr: 1.5, whiteSpace: 'nowrap' }}>
              {ui('admin.signOut')}
            </Button>
          )}
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} size="small">
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.8rem' }}>{getInitials(user?.firstName, user?.lastName)}</Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? open : true}
        onClose={() => setOpen(false)}
        sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRight: '1px solid', borderColor: 'divider' } }}
      >
        {drawer}
      </Drawer>

      <Box component="main" flex={1} minWidth={0} pt="64px">
        <Box p={{ xs: 2, md: 3 }} maxWidth={1300} mx="auto">
          <Outlet />
        </Box>
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Box>
            <Typography fontSize="0.875rem" fontWeight={600}>{user?.firstName} {user?.lastName}</Typography>
            <Typography fontSize="0.75rem" color="text.secondary">{user?.email} · {user?.role}</Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main', gap: 1 }}>
          <LogoutIcon fontSize="small" /> {ui('admin.signOut')}
        </MenuItem>
      </Menu>
    </Box>
  );
}
